import { TriangleAlert, Zap, Wind, Anchor } from 'lucide-react'
import { Section } from '@/components/Section'
import { ConfigHint } from '@/components/ConfigHint'
import { SkeletonBlock } from '@/components/feedback'
import { useAlertCountQuery } from '@/lib/nws/hooks'
import { useSpcQuery, useTropicsQuery, spcLevel, type TropicalSystem } from './pulse-data'
import { threatColor } from '@/lib/criteria/levels'
import { GlobeHero } from '@/features/globe/GlobeHero'
import { useLocationStore } from '@/store/location'
import { hasR2 } from '@/config'
import './pulse.css'

export function PulseView() {
  const count = useAlertCountQuery()
  const spc = useSpcQuery()
  const tropics = useTropicsQuery()
  const saved = useLocationStore((s) => s.saved)

  return (
    <div className="pulse">
      <header className="view-head">
        <h1 className="view-title">Nationwide</h1>
        <p className="view-sub">What’s happening on Earth right now.</p>
      </header>

      <GlobeHero
        location={saved ? { lat: saved.lat, lon: saved.lon, label: saved.label } : undefined}
      />

      {/* National alert pressure — always live, no R2 required */}
      <div className="pulse-national card">
        <div className="pulse-national__main">
          <TriangleAlert size={22} style={{ color: 'var(--status-warn)' }} />
          <span className="pulse-national__num num">{count.data?.total ?? '—'}</span>
          <span className="pulse-national__cap">active alerts nationwide</span>
        </div>
        <div className="pulse-national__split">
          <span>
            <Anchor size={13} /> {count.data?.marine ?? '—'} marine
          </span>
          <span>{count.data?.land ?? '—'} land</span>
        </div>
      </div>

      {/* SPC severe risk */}
      <Section label="Today’s severe risk">
        {!hasR2 ? (
          <ConfigHint envVar="VITE_FEED_SPC" what="the SPC convective outlook" />
        ) : spc.isLoading ? (
          <SkeletonBlock height={120} />
        ) : spc.data ? (
          <div
            className="spc card"
            style={{ '--c': threatColor(spcLevel(spc.data.category)) } as React.CSSProperties}
          >
            <div className="spc__head">
              <Zap size={18} />
              <span className="spc__cat">{spc.data.category}</span>
              <span className="spc__label">{spc.data.label}</span>
            </div>
            {spc.data.summary && <p className="spc__summary">{spc.data.summary}</p>}
            {spc.data.imageUrl && (
              <img src={spc.data.imageUrl} alt="SPC categorical outlook" className="spc__img" />
            )}
          </div>
        ) : (
          <ConfigHint envVar="VITE_FEED_SPC" what="the SPC convective outlook" />
        )}
      </Section>

      {/* Tropics */}
      <Section label="Active tropical systems">
        {!hasR2 ? (
          <ConfigHint envVar="VITE_FEED_TROPICS" what="the global tropical feed" />
        ) : tropics.isLoading ? (
          <SkeletonBlock height={100} />
        ) : tropics.data && tropics.data.length > 0 ? (
          <div className="tropics">
            {tropics.data.map((s) => (
              <TropicCard key={s.id} system={s} />
            ))}
          </div>
        ) : (
          <div className="tropics-quiet card">
            <Wind size={20} />
            <p>No active tropical systems.</p>
          </div>
        )}
      </Section>
    </div>
  )
}

function TropicCard({ system }: { system: TropicalSystem }) {
  const cat = system.category ? `Cat ${system.category}` : system.classification
  return (
    <article className="tropic card">
      <div className="tropic__ring">
        <Wind size={20} />
      </div>
      <div className="tropic__body">
        <span className="tropic__name">{system.name}</span>
        <span className="tropic__meta">
          {[cat, system.basin, system.windMph ? `${Math.round(system.windMph)} mph` : null]
            .filter(Boolean)
            .join(' · ')}
        </span>
      </div>
    </article>
  )
}
